import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import Alert from "@mui/material/Alert";
import { createInventorySync } from "@/api/inventory/createInventorySync";
import { getInventoryList } from "@/api/inventory/getInventoryList";
import type { GetInventoryListArgs } from "@/api/inventory/getInventoryList";
import type { InventoryStatus } from "@/api/inventory/_models/Inventory";
import { useDebouncedValue } from "@/app/useDebouncedValue";
import { ListGrid } from "@/components/list-grid/ListGrid";
import { Page } from "@/components/Page";
import { ListGridFooter } from "@/components/list-grid/ListGridFooter";
import { PageHeader } from "@/components/PageHeader";
import { InventoryListFilterPanel, isInventoryListFilterActive } from "./inventory-list-filter/InventoryListFilterPanel";
import type { InventoryListFilterValues } from "./inventory-list-filter/InventoryListFilterPanel";
import {
  isInventoryListFilterPanelOpenInSearchParams,
  toInventoryListFilterFromSearchParams,
  writeInventoryListFilterToSearchParams,
} from "./inventory-list-filter/inventoryListFilterSearchParams";
import { InventoryListHeader } from "./InventoryListHeader";
import { InventoryListRow } from "./InventoryListRow";
import { InventoryListSkeletonRow } from "./InventoryListSkeletonRow";
import { InventoryListSyncButton } from "./InventoryListSyncButton";
import { InventoryListSynced } from "./InventoryListSynced";

const OPEN_PARAM = "open";
const PAGE_PARAM = "page";
const PAGE_SIZE_PARAM = "pageSize";
const PAGE_SIZES = [50, 100, 200];
const DEFAULT_PAGE_SIZE = 100;

const FILTER_DEBOUNCE_MS = 300;

const toGetInventoryListArgs = (filter: InventoryListFilterValues): GetInventoryListArgs => {
  if (!isInventoryListFilterActive(filter)) return {};
  return {
    search: filter.search.trim() || undefined,
    statuses: filter.statuses,
    locationIds: filter.locationIds,
  };
};

/** The URL's 1-based page as a zero-based index; anything invalid is the first page. */
const toPageFromSearchParams = (params: URLSearchParams): number => {
  const page = Number(params.get(PAGE_PARAM));
  if (!Number.isInteger(page) || page < 1) return 0;
  return page - 1;
};

const toPageSizeFromSearchParams = (params: URLSearchParams): number => {
  const pageSize = Number(params.get(PAGE_SIZE_PARAM));
  if (PAGE_SIZES.includes(pageSize)) return pageSize;
  return DEFAULT_PAGE_SIZE;
};

const toErrorText = (e: unknown): string => {
  const data = (e as { data?: { error?: unknown } })?.data;
  if (data && typeof data.error === "string") return data.error;
  if (e instanceof Error) return e.message;
  return JSON.stringify(e);
};

/** Rewrites the address bar's query string in place (replaceState), without a router navigation. */
const replaceSearchParamsInUrl = (update: (params: URLSearchParams) => void) => {
  const url = new URL(window.location.href);
  update(url.searchParams);
  window.history.replaceState(window.history.state, "", url);
};

/**
 * The Inventory page: a paged grid of levels (GET /inventory-levels) with expandable rows, a filter
 * side panel and "Sync now". Page, page size, expanded rows (?open=) and filters are mirrored in the URL.
 */
export const InventoryList = () => {
  const [searchParams] = useSearchParams();
  const [openIds, setOpenIds] = useState(() => searchParams.getAll(OPEN_PARAM));
  const [filter, setFilter] = useState<InventoryListFilterValues>(() => toInventoryListFilterFromSearchParams(searchParams));
  const [isFilterOpen, setIsFilterOpen] = useState(() => isInventoryListFilterPanelOpenInSearchParams(searchParams));
  const [page, setPage] = useState(() => toPageFromSearchParams(searchParams));
  const [pageSize, setPageSize] = useState(() => toPageSizeFromSearchParams(searchParams));
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const appliedFilter = useDebouncedValue(filter, FILTER_DEBOUNCE_MS);
  const listArgs = useMemo(() => toGetInventoryListArgs(appliedFilter), [appliedFilter]);
  const pageArgs = useMemo(() => ({ ...listArgs, offset: page * pageSize, limit: pageSize }), [listArgs, page, pageSize]);
  const query = getInventoryList.useQuery(pageArgs);
  const [requestSync, syncState] = createInventorySync.useMutation();
  const rows = query.data?.inventory ?? [];
  const total = query.data?.total ?? 0;
  const filterLocations = query.data?.locations ?? [];
  const statusCounts = useMemo(() => {
    const counts: Partial<Record<InventoryStatus, number>> = {};
    for (const entry of query.data?.statusCounts ?? []) {
      if (entry.status) counts[entry.status] = entry.count;
    }
    return counts;
  }, [query.data]);
  const syncs = query.data?.syncs ?? [];
  let footerSummary = `${total.toLocaleString()} rows`;
  if (isInventoryListFilterActive(appliedFilter)) footerSummary = `${total.toLocaleString()} rows match the filters`;
  let emptyText: string | null = null;
  if (rows.length === 0 && !query.isFetching) {
    emptyText = "No inventory yet — Sync now pulls it in.";
    if (isInventoryListFilterActive(appliedFilter)) emptyText = "Nothing matches these filters.";
  }

  const handleFilterToggle = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const handleFilterClose = () => {
    setIsFilterOpen(false);
  };

  const handleFilterChange = (nextFilter: InventoryListFilterValues) => {
    setFilter(nextFilter);
    setPage(0);
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    setPageSize(nextPageSize);
    setPage(0);
  };

  const handleToggle = useCallback((inventoryId: string) => {
    setOpenIds((previous) => {
      if (previous.includes(inventoryId)) return previous.filter((id) => id !== inventoryId);
      return [...previous, inventoryId];
    });
  }, []);

  const handleSync = async () => {
    setSyncMessage(null);
    try {
      const result = await requestSync().unwrap();
      const failures = [
        result.cin7.ok ? null : `Cin7: ${result.cin7.error}`,
        result.estore.ok ? null : `eStore: ${result.estore.error}`,
      ].filter((message) => message !== null);
      if (failures.length > 0) setSyncMessage(failures.join(" · "));
    }
    catch (e) {
      setSyncMessage(toErrorText(e));
    }
  };

  const handleCloseSyncMessage = () => {
    setSyncMessage(null);
  };

  useEffect(() => {
    if (!query.data || query.isFetching) return;
    const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [page, pageSize, query.data, query.isFetching, total]);

  useEffect(() => {
    replaceSearchParamsInUrl((params) => {
      params.delete(OPEN_PARAM);
      for (const id of openIds) params.append(OPEN_PARAM, id);
    });
  }, [openIds]);

  useEffect(() => {
    replaceSearchParamsInUrl((params) => writeInventoryListFilterToSearchParams(params, filter, isFilterOpen));
  }, [filter, isFilterOpen]);

  useEffect(() => {
    replaceSearchParamsInUrl((params) => {
      params.delete(PAGE_PARAM);
      params.delete(PAGE_SIZE_PARAM);
      if (page > 0) params.set(PAGE_PARAM, String(page + 1));
      if (pageSize !== DEFAULT_PAGE_SIZE) params.set(PAGE_SIZE_PARAM, String(pageSize));
    });
  }, [page, pageSize]);

  if (query.isError) {
    return (
      <Alert severity="error">
        Failed to load inventory: {toErrorText(query.error)}
      </Alert>
    );
  }

  return (
    <>
      <InventoryListFilterPanel
        filter={filter}
        isOpen={isFilterOpen}
        locations={filterLocations}
        onChange={handleFilterChange}
        onClose={handleFilterClose}
        statusCounts={statusCounts}
      />
      <Page isLocked>
        <PageHeader
          subtitle={<InventoryListSynced syncs={syncs} />}
          title="Inventory"
        >
          <InventoryListSyncButton
            isSyncing={syncState.isLoading}
            onSync={handleSync}
          />
        </PageHeader>
        <ListGrid
          emptyText={emptyText}
          footer={(
            <ListGridFooter
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              page={page}
              pageSize={pageSize}
              pageSizes={PAGE_SIZES}
              summary={footerSummary}
              total={total}
            />
          )}
          header={(
            <InventoryListHeader
              isFilterActive={isInventoryListFilterActive(filter)}
              onFilterToggle={handleFilterToggle}
            />
          )}
          isFetching={query.isFetching || syncState.isLoading}
          isLoading={query.isLoading}
          notice={syncMessage && (
            <Alert
              onClose={handleCloseSyncMessage}
              severity="error"
              sx={{ flexShrink: 0 }}
            >
              {syncMessage}
            </Alert>
          )}
          skeletonRow={<InventoryListSkeletonRow />}
        >
          {rows.map((row) => (
            <InventoryListRow
              isOpen={openIds.includes(row.id)}
              key={row.id}
              onToggle={handleToggle}
              row={row}
            />
          ))}
        </ListGrid>
      </Page>
    </>
  );
};
